import dominantEnergyMeanings from '../data/dominantEnergyMeanings';
import movingLineMeanings from '../data/movingLineMeanings';
import { getPhilosophyContent } from '../data/thamPhaiHieu.js';

const getMovingLineMeaning = (hexagramName, lineNumber) => {
  if (!movingLineMeanings[hexagramName] || !movingLineMeanings[hexagramName][lineNumber]) {
    return 'Thông tin chuyên sâu về hào động đang được cập nhật';
  }
  return movingLineMeanings[hexagramName][lineNumber];
};

const formatPhilosophyContent = (philosophy) => {
  if (!philosophy) return '';
  
  const { mainPillars } = philosophy;
  
  const formatKeyPoints = (points) => {
    return points.map(point => {
      let content = `#### ${point.title}\n`;
      
      if (point.aspects) {
        Object.entries(point.aspects).forEach(([key, value]) => {
          if (value.personalityTraits) {
            content += `- **Khía cạnh cá nhân:**\n${value.personalityTraits.map(trait => `  • ${trait}`).join('\n')}\n`;
          }
          if (value.coreValues) {
            content += `- **Giá trị cốt lõi:**\n${value.coreValues.map(val => `  • ${val}`).join('\n')}\n`;
          }
        });
      }
      
      if (point.methods) {
        if (Array.isArray(point.methods)) {
          content += `- **Phương pháp:**\n${point.methods.map(method => `  • ${method}`).join('\n')}\n`;
        } else if (point.methods.approaches) {
          content += `- **Cách tiếp cận:**\n${point.methods.approaches.map(approach => `  • ${approach}`).join('\n')}\n`;
        }
      }
      
      if (point.humanNature) {
        content += `- **Bản chất con người:**\n`;
        content += `  • ${point.humanNature.emotions}\n`;
        content += `  • ${point.humanNature.inherentNature}\n`;
      }
      
      return content;
    }).join('\n');
  };

  let content = `### ${philosophy.title}\n\n`;
  
  // Format Tham Phải Hiểu section
  content += `### ${mainPillars.thamPhaiHieu.title}\n`;
  content += formatKeyPoints(mainPillars.thamPhaiHieu.keyPoints);
  
  // Format Chọn Đúng Người section
  content += `\n### ${mainPillars.chonDungNguoi.title}\n`;
  mainPillars.chonDungNguoi.keyChoices.forEach(choice => {
    content += `#### ${choice.title}\n`;
    content += `- ${choice.description}\n`;
    if (choice.importance) content += `- Tầm quan trọng: ${choice.importance}\n`;
    if (choice.details) {
      content += `- Chi tiết:\n`;
      Object.entries(choice.details).forEach(([key, value]) => {
        if (typeof value === 'string') {
          content += `  • ${value}\n`;
        }
      });
    }
  });

  // Format Sống Như Ý section
  content += `\n### ${mainPillars.songNhuY.title}\n`;
  content += `Nguyên tắc: ${mainPillars.songNhuY.principles.join(', ')}\n`;

  // Format Giới-Định-Tuệ section
  content += `\n### ${mainPillars.gioiDinhTue.title}\n`;
  Object.entries(mainPillars.gioiDinhTue.elements).forEach(([key, element]) => {
    content += `#### ${element.title}\n`;
    content += `- ${element.description}\n`;
    if (element.principles) {
      content += `- Nguyên tắc:\n${element.principles.map(p => `  • ${p}`).join('\n')}\n`;
    }
    if (element.benefits) {
      content += `- Lợi ích: ${element.benefits}\n`;
    }
    if (element.relationship) {
      content += `- Mối quan hệ:\n${element.relationship.map(r => `  • ${r}`).join('\n')}\n`;
    }
  });

  return content;
};

export const generatePrompt = (hexagramData, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice) => {
  const dominantEnergyMeaning = dominantEnergy
    ? `Năng lượng chủ đạo **${dominantEnergy}** chi phối toàn bộ dãy số: 
       """${dominantEnergyMeanings[dominantEnergy]?.description || 'Đang hoàn thiện dữ liệu chuyên sâu'}"""`
    : 'Không xác định được năng lượng chủ đạo';

  const formatNotifications = (notifications) => {
    if (!notifications?.length) return '';
    return notifications.map(item => 
      `▸ **${item.code}** (${item.description}): ${item.meaning?.replace(/\n\s*-/g, ' ').replace(/\n+/g, ' ') || 'Phân tích chi tiết đang được cập nhật'}`
    ).join('\n\n');
  };

  const formatGhiChu = (warnings) => {
    if (!warnings?.length) return '';
    return warnings.map(item => 
      item.title.includes('nhiều 0') 
        ? `**${item.title}**: ${item.description} - Cảnh báo đặc biệt cần xem xét kỹ lưỡng`
        : `**${item.title}**: ${item.description}`
    ).join('. ');
  };

  const formatCombinedPairs = (pairs) => {
    if (!pairs?.length) return '';
    return pairs.map(pair => {
      const meaning = pair.meaning?.split('\n').map(line => 
        line.trim().replace(/^-/, '').replace(/:/, ': ')
      ).join(' ') || 'Ý nghĩa chuyên sâu đang được hoàn thiện';
      return `**${pair.number}** (${pair.energy}): ${meaning}`;
    }).join('\n\n');
  };

  const formatNumberPairs = (pairs) => {
    if (!pairs?.length) return 'Không phát hiện tổ hợp đặc biệt';
    return pairs.map(pair => 
      `▸ **${pair.pair}** (quẻ ${pair.hexagram}): ${pair.hexagramMeaning}`
    ).join('\n');
  };

  const formatEnergyDetails = (pairs) => {
    if (!pairs?.length) return 'Không xác định được dòng chảy năng lượng';
    
    const items = pairs.map(p => `${p.number} (${p.energy})`);
    if (items.length === 1) return `Bắt đầu và kết thúc với **${items[0]}**`;
    
    return items.slice(0, -1).join(', ') + ` và cuối cùng là **${items[items.length-1]}**`;
  };

  let additionalNotices = [];
  if (notificationList?.length) {
    additionalNotices.push(`**Tổ hợp cảnh báo**:\n${formatNotifications(notificationList)}`);
  }
  if (ghiChuList?.length) {
    additionalNotices.push(`**Ghi chú đặc biệt**: ${formatGhiChu(ghiChuList)}`);
  }
  if (warningNotice) {
    additionalNotices.push(`**Cảnh báo số đuôi**: ${warningNotice.title} - ${warningNotice.subtitle}\n${warningNotice.points.map(p => `▸ ${p}`).join('\n')}`);
  }

  const movingLineMeaning = getMovingLineMeaning(hexagramData.main.name, hexagramData.movingLine);

  const philosophyContent = getPhilosophyContent();
  const formattedPhilosophy = formatPhilosophyContent(philosophyContent);
  const philosophySection = formattedPhilosophy ? `
### Triết lý "Tham Phải Hiểu" áp dụng cho trường hợp này

${formattedPhilosophy}` : '';

  return `Phân tích tâm lý chuyên sâu về khách hàng để thấu hiểu động cơ, hành vi và đưa ra khuyến nghị hành động, trình bày bằng ngôn ngữ dễ hiểu cho người không chuyên về tâm lý học.
<input>
${businessContext ? `- **Bối cảnh kinh doanh**: ${businessContext}` : ''}
${interactionGoal ? `- **Mục tiêu giao dịch**: ${interactionGoal}` : ''}

Thông tin khách hàng:
- **SĐT**: ${hexagramData.phoneNumber}
- **Nhân khẩu học**: ${gender}, ${ageRange}, ${maritalStatus}
- **Công việc chính**: ${job || 'Chưa xác định'}
Thông tin quẻ Kinh Dịch:
- **Quẻ chính**: ${hexagramData.main.name} 
  → *${hexagramData.main.meaning}*
- **Hào động ${hexagramData.movingLine}**: 
  → *${movingLineMeaning}*
- **Quẻ biến**: ${hexagramData.transformed.name} 
  → *${hexagramData.transformed.meaning}*
- **Các quẻ Dịch then chốt**:
${formatNumberPairs(hexagramData.numberPairs)}
Thông tin năng lượng số:
- **Chuỗi tổ hợp năng lượng số**:
${formatEnergyDetails(combinedPairs)}
- **Năng lượng chủ đạo**:
${dominantEnergyMeaning}
- **Phân tích từng tổ hợp năng lượng số**:
${formatCombinedPairs(combinedPairs)}
- **Cảnh báo đặc biệt**:
${additionalNotices.join('\n\n') || 'Không có cảnh báo đặc biệt'}
</input>

Dựa trên bối cảnh kinh doanh và mục tiêu tương tác đã xác định, cùng với thông tin chi tiết về khách hàng được cung cấp ở phần <input>, hãy đóng vai trò là chuyên gia tâm lý học DAVID J. LIEBERMAN, PhD để thực hiện phân tích chuyên sâu về khách hàng này. Mục tiêu là cung cấp một cái nhìn toàn diện và dễ hiểu về các khía cạnh tâm lý quan trọng của khách hàng, giúp người không có kiến thức chuyên môn về tâm lý học cũng có thể nắm bắt và ứng dụng hiệu quả.

# Các bước phân tích

Phân tích chi tiết theo các khía cạnh sau, đảm bảo mỗi phân tích đều dựa trên thông tin đã cung cấp và được lý giải một cách logic, dễ hiểu:

**1. PHÂN TÍCH TỔNG QUAN**

*   **Đặc điểm nổi bật trong tính cách và phong cách làm việc:**  Phân tích và mô tả những nét tính cách chủ đạo, cách khách hàng thể hiện bản thân trong công việc và giao tiếp chuyên nghiệp.
*   **Xu hướng ra quyết định và điểm mấu chốt ảnh hưởng đến quyết định:**  Lý giải cách khách hàng thường đưa ra quyết định, yếu tố nào có trọng lượng lớn nhất trong quá trình này (ví dụ: lý trí, cảm xúc, dữ liệu, kinh nghiệm cá nhân, v.v.).
*   **Những điểm mạnh và điểm yếu cần lưu ý khi tương tác:**  Xác định các điểm mạnh của khách hàng có thể tận dụng và điểm yếu cần lưu ý để xây dựng mối quan hệ và giao tiếp hiệu quả.

**2. PHÂN TÍCH CHUYÊN SÂU**

*   **Khía cạnh cá nhân:**
    *   **Nỗi sợ cơ bản:** Phân tích và diễn giải nỗi sợ sâu thẳm, thường trực chi phối hành vi và quyết định của khách hàng.
    *   **Khao khát cơ bản:**  Xác định động lực, mong muốn cốt lõi mà khách hàng luôn hướng tới.
    *   **Điều coi trọng nhất:**  Chỉ ra giá trị, nguyên tắc mà khách hàng đặt lên hàng đầu trong cuộc sống và công việc.
    *   **Cách giao tiếp với người khác:**  Mô tả phong cách giao tiếp đặc trưng của khách hàng (ví dụ: trực tiếp, gián tiếp, hòa đồng, dè dặt, v.v.).
    *   **Phản ứng với căng thẳng:**  Phân tích cách khách hàng đối phó với áp lực, stress và những tình huống khó khăn.
    *   **Điều cảm thấy tổn thương nhất:**  Xác định những yếu tố, hành động có thể gây tổn thương hoặc làm mất lòng tin của khách hàng.
    *   **Cách đưa quyết định (chi tiết hơn):**  Phân tích sâu hơn về quy trình ra quyết định, các bước khách hàng thường trải qua, thông tin cần thiết để đưa ra quyết định.
    *   **Dấu hiệu báo động:**  Nhận diện những dấu hiệu, hành vi bất thường có thể cho thấy khách hàng đang gặp vấn đề hoặc không hài lòng.

*   **Giá trị cốt lõi:**
    *   **Giá trị cốt lõi (chi tiết hơn):**  Phân tích sâu hơn về hệ thống giá trị, niềm tin chi phối hành động và thái độ của khách hàng.
    *   **Thế mạnh, điểm yếu (chi tiết hơn):**  Phân tích chi tiết hơn về các kỹ năng, phẩm chất nổi trội và hạn chế của khách hàng trong bối cảnh kinh doanh.
    *   **Niềm tin/Đức tin:**  Tìm hiểu về hệ thống niềm tin, quan điểm tôn giáo (nếu có) có thể ảnh hưởng đến quyết định và hành vi của khách hàng.
    *   **Chủ đề quan tâm:**  Xác định những lĩnh vực, vấn đề mà khách hàng đặc biệt quan tâm và dành sự chú ý.
    *   **Điều muốn làm:**  Phân tích động lực, mục tiêu dài hạn mà khách hàng muốn đạt được trong công việc và cuộc sống.

**3. ỨNG DỤNG VÀ KHUYẾN NGHỊ HÀNH ĐỘNG**

Dựa trên các phân tích trên, đưa ra các nhận định, lý giải chi tiết và **KHUYẾN NGHỊ HÀNH ĐỘNG CỤ THỂ**:

*   **Ảnh hưởng đến phong cách giao tiếp:**  Giải thích các yếu tố tâm lý ảnh hưởng đến cách giao tiếp của khách hàng và cách điều chỉnh giao tiếp để tương tác hiệu quả hơn.
*   **Tác động đến quá trình ra quyết định:**  Làm rõ các yếu tố tâm lý tác động đến quyết định của khách hàng trong bối cảnh kinh doanh.
*   **Điểm cần khai thác/tránh khi tương tác:**  Chỉ ra những khía cạnh nên tập trung khai thác và những điều cần tránh để xây dựng mối quan hệ tốt đẹp và đạt được mục tiêu.
*   **Chiến lược tiếp cận, bán hàng, đàm phán:**  Đề xuất các chiến lược tiếp cận, bán hàng, đàm phán phù hợp, dựa trên phân tích tâm lý khách hàng và bối cảnh kinh doanh, để đạt được mục tiêu tương tác.
*   **KHUYẾN NGHỊ QUAN TRỌNG:**
    *   **Những điều tuyệt đối nên làm:**  Liệt kê các hành động, cách tiếp cận nên áp dụng để tương tác thành công với khách hàng.
    *   **Những điều tuyệt đối nên tránh:**  Liệt kê các hành động, cách tiếp cận cần tránh để không gây mất lòng tin hoặc ảnh hưởng tiêu cực đến mối quan hệ.
    *   **Các yếu tố then chốt quyết định thành công:**  Xác định các yếu tố quan trọng nhất, mang tính quyết định đến sự thành công trong việc đạt được mục tiêu kinh doanh khi tương tác với khách hàng này.

# Định dạng đầu ra

*   **Ngôn ngữ:**  Sử dụng tiếng Việt, ngôn ngữ diễn đạt rõ ràng, mạch lạc, dễ hiểu đối với người không có kiến thức chuyên sâu về tâm lý học.
*   **Cấu trúc:**  Tuân thủ cấu trúc phân tích theo các phần và mục đã nêu trên.
*   **Chi tiết:**  Đảm bảo mỗi phân tích, lý giải và khuyến nghị đều chi tiết, cụ thể và có cơ sở từ thông tin <input> đã cung cấp.
*   **Khuyến nghị:**  Phần "KHUYẾN NGHỊ QUAN TRỌNG" cần được trình bày dưới dạng danh sách gạch đầu dòng, ngắn gọn, dễ áp dụng.

# Ví dụ

**Một phần phân tích mẫu (ví dụ placeholder - phần "Nỗi sợ cơ bản"):**

**Nỗi sợ cơ bản:** Dựa trên thông tin về tính cách cẩn trọng, làm việc theo quy trình và coi trọng sự chính xác, có thể thấy nỗi sợ cơ bản của ông Nguyễn Văn A có thể là **sợ sai sót, sợ mất kiểm soát hoặc sợ sự không chắc chắn**.  Trong bối cảnh công việc Marketing, nỗi sợ này có thể biểu hiện qua việc ông A luôn muốn có kế hoạch chi tiết, kiểm soát chặt chẽ các chiến dịch và lo lắng về những rủi ro tiềm ẩn.  Hiểu được nỗi sợ này giúp chúng ta giao tiếp với ông A bằng cách cung cấp thông tin rõ ràng, chi tiết, có bằng chứng và đảm bảo tính chính xác, đồng thời thể hiện sự chuẩn bị kỹ lưỡng và khả năng kiểm soát rủi ro trong các đề xuất hợp tác.

**(Lưu ý: Ví dụ trên chỉ là placeholder minh họa, ví dụ thực tế cần phân tích chi tiết và đầy đủ hơn dựa trên thông tin khách hàng cụ thể.)**

# Lưu ý

*   **Tính bảo mật:**  Đảm bảo tính bảo mật thông tin khách hàng trong quá trình phân tích.
*   **Khách quan:**  Phân tích khách quan, dựa trên thông tin đã cung cấp, tránh đưa ra nhận định chủ quan hoặc áp đặt.
*   **Ứng dụng thực tế:**  Tập trung vào việc đưa ra các khuyến nghị có tính ứng dụng cao, giúp người dùng có thể áp dụng ngay vào thực tế tương tác với khách hàng để đạt được mục tiêu kinh doanh.
`;
};